import { INVOICE_STATUS } from '@monorepo/shared/schemas/common'
import type { Invoice } from '@monorepo/shared/schemas/invoices'
import {
  AlertTriangle,
  Ban,
  CheckCircle,
  CreditCard,
  FileCheck,
} from 'lucide-react'
import { Badge } from '@/shared/components/ui/badge'
import { Button } from '@/shared/components/ui/button'
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/shared/components/ui/card'

const formatLabel = (value: string) =>
  value
    .split('_')
    .map(w => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ')

interface InvoiceStatusActionsProps {
  invoice: Invoice
  onFinalize: () => void
  onRecordPayment: () => void
  onMarkAsPaid: () => void
  onVoid: () => void
  loading?: boolean
}

export function InvoiceStatusActions({
  invoice,
  onFinalize,
  onRecordPayment,
  onMarkAsPaid,
  onVoid,
  loading = false,
}: InvoiceStatusActionsProps) {
  const isDraft = invoice.status === INVOICE_STATUS.draft
  const isVoid = invoice.status === INVOICE_STATUS.void
  const isPaid = invoice.payment_status === 'paid'
  const canRecordPayment = !isPaid && !isVoid && !isDraft
  const canFinalize = isDraft
  const canVoid = !isVoid

  return (
    <Card>
      <CardHeader className='pb-3'>
        <CardTitle className='text-sm font-medium'>Status</CardTitle>
      </CardHeader>
      <CardContent className='space-y-4'>
        <div className='flex items-center gap-2'>
          <Badge variant={isVoid ? 'destructive' : 'secondary'}>
            {formatLabel(invoice.status)}
          </Badge>
          <Badge
            variant={
              isPaid
                ? 'default'
                : invoice.payment_status === 'overdue'
                  ? 'destructive'
                  : 'outline'
            }
          >
            {formatLabel(invoice.payment_status)}
          </Badge>
        </div>

        {isVoid && (
          <div className='p-3 bg-destructive/10 rounded-md text-sm space-y-1'>
            <p className='font-medium text-destructive'>Invoice Voided</p>
            {Boolean((invoice as Record<string, unknown>).void_reason) && (
              <p className='text-muted-foreground'>
                Reason:{' '}
                {String((invoice as Record<string, unknown>).void_reason)}
              </p>
            )}
            {Boolean((invoice as Record<string, unknown>).voided_at) && (
              <p className='text-muted-foreground text-xs'>
                Voided on{' '}
                {new Date(
                  String((invoice as Record<string, unknown>).voided_at)
                ).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'short',
                  day: 'numeric',
                })}
              </p>
            )}
          </div>
        )}

        {!isVoid && (
          <div className='space-y-2'>
            {canFinalize && (
              <Button
                onClick={onFinalize}
                disabled={loading}
                className='w-full gap-2'
              >
                <FileCheck className='w-4 h-4' />
                Finalize Invoice
              </Button>
            )}

            {canRecordPayment && (
              <>
                <Button
                  onClick={onRecordPayment}
                  disabled={loading}
                  variant='outline'
                  className='w-full gap-2'
                >
                  <CreditCard className='w-4 h-4' />
                  Record Payment
                </Button>
                <Button
                  onClick={onMarkAsPaid}
                  disabled={loading}
                  variant='outline'
                  className='w-full gap-2'
                >
                  <CheckCircle className='w-4 h-4' />
                  Mark as Paid
                </Button>
              </>
            )}

            {canVoid && (
              <Button
                onClick={onVoid}
                disabled={loading}
                variant='outline'
                className='w-full gap-2 text-destructive hover:text-destructive'
              >
                <Ban className='w-4 h-4' />
                Void Invoice
              </Button>
            )}

            {isPaid && canVoid && (
              <p className='text-xs text-muted-foreground flex items-center gap-1'>
                <AlertTriangle className='w-3 h-3' />
                Voiding a paid invoice may require a refund
              </p>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  )
}
